import def from "csi/def";
import { View } from "csi/def/View";
import html from "csi/html";
import csi from "csi/main";
import reflection from "csi/reflection";
import { sessionStore } from "csi/state/rootStore";
import util from "csi/util";
import { selectElementByXpath } from "csi/utility/defUtility";
import { useEventListener } from "designSystem/hooks";
import _ from "lodash";
import React, { FunctionComponent, useCallback, useRef } from "react";
import xmlFormat from "xml-formatter";

/** document containing XML view definitions when loading from merged file */
let _cachedMergedConfig: Element;

async function loadMergedConfig(): Promise<boolean> {
	if (_cachedMergedConfig === undefined) {
		try {
			const xmlDocument = await csi.loadXml(csi.urlForGenerated("Site_Merged", true));
			_cachedMergedConfig = xmlDocument.documentElement;
		} catch (err) {
			_cachedMergedConfig = null;
		}
	}

	return true;
}

/**
 * Create indentation string based on number of indent levels
 * @param levels 
 * @returns 
 */
function _indent(levels: number): string {
	const spacesPerLevel = _cachedMergedConfig ? 2 : 4;
	return new Array(levels * spacesPerLevel).join(" ");
}

function _renderXml(id: string, idType: string, xmlNode: Element, domNode: HTMLElement, indentLevel: number) {
	if (!xmlNode) {
		return;
	}

	const titleNode = html.create("div", { class: "debugViewTitle" }, domNode);
	html.create("label", { innerHTML: "Id: " }, titleNode);
	html.create("span", { innerHTML: id }, titleNode);
	html.create("label", { innerHTML: "Type: " }, titleNode);
	html.create("span", { innerHTML: idType }, titleNode);
	html.create("label", { innerHTML: "Module: " }, titleNode);

	if (sessionStore.mergedConfig) {
		const moduleName = xmlNode.getAttribute("_moduleName");
		if (moduleName) {
			html.create("span", { innerHTML: moduleName }, titleNode);
		}
	} else {
		const url = xmlNode.baseURI;
		html.a({
			href: url,
			innerHTML: url,
			target: "DebugXML",
		}, titleNode);
	}

	// format XML - use xml:space=preserve so empty lines are preserved, but that then causes us to
	// fix leading whitespace below
	let sXml = util.serializeXML(xmlNode, true);
	sXml = xmlFormat(sXml);

	// remove _moduleName property inserted by Update Configuration
	sXml = sXml.replace(/ _moduleName=\".*?\"/, "");
	
	// also remove xml properties
	sXml = sXml.replace(/ xmlns:xsi=\".*?\"/, "");
	sXml = sXml.replace(/ xml:space=\".*?\"/, "");

	// if using merged XML, then change 2 space tabs to 4
	if (_cachedMergedConfig) {
		sXml = sXml.replace(/  /g, "    ");
	}

	// remove leading whitespace
	sXml = sXml.replace(/\t/g, "    ");
	sXml = sXml.replace((indentLevel == 2) ? /\n        /g : /\n            /g, "\n");
	
	html.create("pre", { innerText: sXml }, domNode);
}

async function _renderViewXml(defView: View, element: HTMLElement): Promise<boolean> {
	const idType = (def.getClassViewDef(reflection.getClass(defView.getTypeNode()), defView.getId(), false, true) as unknown as string) || defView.getTypeNode();
	const defType = def.typeDefinition(idType);

	await loadMergedConfig();

	if (!_cachedMergedConfig || !sessionStore.mergedConfig) {
		// if it couldn't load the Site_Merged.xml, or the SITE_MERGED=false on configuration.bat, pull from the generated view tags...
		const xmlView = defType ? defType.getViewTag(defView.m_id) as Element : null;
		if (!xmlView) {
			return false;
		}

		_renderXml(defView.m_id, idType, xmlView, element, 2);

		_.each(defView.usedCards, (card) => {
			_renderXml(card.m_id, card.m_idType, card._xn, element, 2);
		});

		def.traverseHeadings(defView.getHeadings(), (h) => {
			_renderXml(h.m_id, h.definedOnNodeType, h._xn, element, 3);
		});
	} else {
		// parse fileXML to look for XPath /NodeRef[@Type=idType]/View[@Id=defView.m_id]
		const xPath = "/Node[@Type=\"" + idType + "\"]/View[@Id=\"" + defView.m_id + "\"]";
		const xmlView = selectElementByXpath(_cachedMergedConfig, xPath) as Element;
		if (!xmlView) {
			return false;
		}

		_renderXml(defView.m_id, idType, xmlView, element, 2);

		_.each(defView.usedCards, (card) => {
			const xPath = "/Node[@Type=\"" + card.m_idType + "\"]/Card[@Id=\"" + card.m_id + "\"]";
			const xmlCard = selectElementByXpath(_cachedMergedConfig, xPath) as Element;
			_renderXml(card.m_id, card.m_idType, xmlCard, element, 2);
		});

		def.traverseHeadings(defView.getHeadings(), (h) => {
			if (h.definedOnNodeType) {
				const xPath = "/Node[@Type=\"" + h.definedOnNodeType + "\"]/Headings/Heading[@Id=\"" + h.m_id + "\"]";
				const xmlHeading = selectElementByXpath(_cachedMergedConfig, xPath) as Element;
				_renderXml(h.m_id, h.definedOnNodeType, xmlHeading, element, 3);
			}
		});
	}

	return true;
}

/** render XML view definition for given view */
export async function renderViewXml(defView: View, contentNode: HTMLElement, pos?: string): Promise<HTMLElement> | undefined {
	const element = html.create("div", {
		class: "debugView",
		tabindex: 0,
	}, contentNode, pos);

	const found = await _renderViewXml(defView, element);
	if (found) {
		return element;
	}

	html.orphan(element);
}

export interface ViewXmlProps {
	/** node type owning view */
	nodeType: string;

	/** callback for closing XML */
	onClose: () => void;

	/** view id */
	viewId: string;
}

export const ViewXml: FunctionComponent<ViewXmlProps> = (props) => {
	const { nodeType, onClose, viewId } = props;
	const outputRef = useRef<HTMLElement>();

	const contentRef = useCallback(async (domNode) => {
		if (domNode) {
			const defView = def.getClassViewDef(reflection.getClass(nodeType), viewId) as View;
			outputRef.current = await renderViewXml(defView, domNode);
		} else if (outputRef.current) {
			html.orphan(outputRef.current);
		}
	}, [nodeType, viewId]);

	const onKeyUp = (event: Event) => {
		const keyboardEvent = event as KeyboardEvent;
		if (keyboardEvent.key === "F2") {
			onClose();
			event.stopPropagation();
		}
	};

	useEventListener("keyup", onKeyUp, outputRef);

	return (
		<div ref={contentRef} />
	);
};

export default ViewXml;

